import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { LoraModelSummaryDto } from '../../api/queries/lora';
import type { TrainingProgressEvent } from '../../hooks/useTrainingProgress';

interface LoraModelCardProps {
  model: LoraModelSummaryDto;
  liveProgress: TrainingProgressEvent | null;
  onCancel: (loraId: string) => void;
  onRequeue: (loraId: string) => void;
  onViewDetail: (loraId: string) => void;
}

const STATUS_COLORS: Record<
  string,
  'default' | 'primary' | 'success' | 'error' | 'warning'
> = {
  queued: 'default',
  training: 'primary',
  complete: 'success',
  failed: 'error',
  approved: 'success',
  rejected: 'warning',
  cancelled: 'default',
};

const PHASE_LABELS: Record<string, string> = {
  download: 'Downloading images',
  caption: 'Captioning with Florence-2',
  configure: 'Configuring trainer',
  training: 'Training LoRA',
  bundling: 'Bundling dataset',
  uploading: 'Uploading results',
};

export function LoraModelCard({
  model,
  liveProgress,
  onCancel,
  onRequeue,
  onViewDetail,
}: LoraModelCardProps) {
  const isActive =
    model.trainingStatus === 'training' || model.trainingStatus === 'queued';
  const isFailed =
    model.trainingStatus === 'failed' || model.trainingStatus === 'cancelled';
  const progress = liveProgress?.progress ?? model.progress;
  const phase = liveProgress?.phase ?? model.currentPhase;

  const handleCopyTrigger = () => {
    navigator.clipboard.writeText(model.triggerWord);
  };

  return (
    <Card
      variant="outlined"
      sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
      onClick={() => onViewDetail(model.id)}
    >
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">
              {model.name}
              {model.version && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  {model.version}
                </Typography>
              )}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={model.trainingStatus}
                color={STATUS_COLORS[model.trainingStatus] ?? 'default'}
                size="small"
              />
              {model.trainer && (
                <Typography variant="caption" color="text.secondary">
                  {model.trainer}
                </Typography>
              )}
            </Stack>
          </Stack>

          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Copy trigger word">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyTrigger();
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isActive && (
              <Tooltip title="Cancel training">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(model.id);
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isFailed && (
              <Tooltip title="Requeue training">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequeue(model.id);
                  }}
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            color: 'text.secondary',
            display: 'block',
            mt: 0.5,
          }}
        >
          {model.triggerWord}
        </Typography>

        {model.trainingStatus === 'training' && (
          <Box sx={{ mt: 1.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                {phase ? (PHASE_LABELS[phase] ?? phase) : 'Starting...'}
              </Typography>
              {liveProgress?.etaSeconds != null &&
                liveProgress.etaSeconds > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ETA: {formatEta(liveProgress.etaSeconds)}
                  </Typography>
                )}
            </Stack>
            <LinearProgress
              variant={progress != null ? 'determinate' : 'indeterminate'}
              value={(progress ?? 0) * 100}
            />
            {liveProgress?.message && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {liveProgress.message}
              </Typography>
            )}
          </Box>
        )}

        {model.trainingStatus === 'queued' && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Waiting for GPU worker...
          </Typography>
        )}

        {model.qualityScore != null && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Quality: {(model.qualityScore * 100).toFixed(0)}%
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
